import React, { useCallback, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Path } from 'constants/router'
import type { CountryCode } from 'models/purchaseFlowRevamp'
import { navigateWithSearchHash } from 'utils/helpers'
import { discardOrderAttempt } from 'utils/analytics/orderAttempt'
import { usePfEntryNavigation } from 'components/pages/PurchaseFlowRevamp/entry/usePfEntryNavigation'
import { missingPropertyFields } from 'components/pages/PurchaseFlowRevamp/entry/usePropertyCompleteness'
import { PropertySelectionDrawer } from 'components/pages/PurchaseFlowRevamp/entry/PropertySelectionDrawer.component'
import { usePfStore } from 'components/pages/PurchaseFlowRevamp/store'
import { useUserData } from 'components/contexts/UserDataContext'

import { useClientProperty } from './contexts'

/**
 * Starts an order for the property whose page this is, the same way from every order button on it.
 *
 * With the revamped flow the property is preselected and the client lands on the catalogue; a property
 * missing the details the session needs opens the selection drawer locked to it instead. The legacy
 * flow, and the revamped one before the property has loaded, receive the property as `?realtyId`. So does a
 * property outside the selected workspace: the revamped flow opens its session there, which the backend refuses.
 * Render `completionDrawer` next to the button.
 *
 * @example
 * const { startOrder, completionDrawer } = useStartOrderForProperty()
 * <MUIButton onClick={startOrder} />{completionDrawer}
 */
export const useStartOrderForProperty = (): { startOrder: () => void, completionDrawer: React.ReactNode } => {
  const navigate = useNavigate()
  const location = useLocation()
  const { propertyId, propertyData, propertyFullAddress, resolvedWorkspaceId } = useClientProperty()
  const { userPropertyWorkspaceId } = useUserData()
  const { isEnabled: isPfRevampEnabled } = usePfEntryNavigation()
  const setProperty = usePfStore((s) => s.setProperty)
  const [isCompletionOpen, setIsCompletionOpen] = useState(false)
  // The drawer fetches as soon as it mounts, so it stays out of the page until it is first opened.
  const [hasCompletionOpened, setHasCompletionOpened] = useState(false)

  const startOrder = useCallback(() => {
    const isInSelectedWorkspace = !resolvedWorkspaceId || resolvedWorkspaceId === userPropertyWorkspaceId
    if (isPfRevampEnabled && propertyData?.id && isInSelectedWorkspace) {
      if (missingPropertyFields(propertyData).length > 0) {
        setIsCompletionOpen(true)
        setHasCompletionOpened(true)
        return
      }
      setProperty({
        id: propertyData.id,
        addressLabel: propertyFullAddress,
        countryCode: (propertyData.address?.country ?? 'DE') as CountryCode,
      })
      navigate(Path.PURCHASE_FLOW_REVAMP_CATALOGUE)
      return
    }

    navigate(
      { pathname: Path.PURCHASE_FLOW, search: `?realtyId=${propertyId}` },
      { state: { redirectedFrom: navigateWithSearchHash(location) } }
    )
  }, [isPfRevampEnabled, propertyData, propertyFullAddress, resolvedWorkspaceId, userPropertyWorkspaceId, setProperty, navigate, propertyId, location])

  const completionDrawer = propertyData && hasCompletionOpened
    ? (
      <PropertySelectionDrawer
        isOpen={isCompletionOpen}
        onClose={() => setIsCompletionOpen(false)}
        onDismiss={discardOrderAttempt}
        onProceed={() => navigate(Path.PURCHASE_FLOW_REVAMP_CATALOGUE)}
        lockedProperty={propertyData}
      />
    )
    : null

  return { startOrder, completionDrawer }
}
